import { Component, ChangeDetectionStrategy, inject, computed, effect, signal  } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CallStoreService } from '../../services/call-store.service';
import { CallAudioService } from '../../services/call-audio.service';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { IncomingCallModalComponent } from '../incoming-call-modal/incoming-call-modal.component';

import { ZimService } from '../../../../services/zim.service';
import { CallActionData, CallInvitation } from '../../types/call';
import { ZIMUserFullInfo } from 'zego-zim-web';
import { BroadcastService } from '../../../../services/broadcast.service';
import { LocalMediaPermissionService } from '../../../../services/local-media-permission.service';
import { ModalPermissionComponent } from '../../../meeting-management/components/modal-permission/modal-permission.component';

export type IncomingCallState =
  | { status: 'idle' }
  | { status: 'loading'; call: CallInvitation }
  | { status: 'ready'; call: CallInvitation; inviter: ZIMUserFullInfo };
@Component({
  selector: 'app-call-status-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './call-status-bar.component.html',
  styleUrls: ['./call-status-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})


export class CallStatusBarComponent {
  private readonly callStore = inject(CallStoreService);
  private readonly callAudio = inject(CallAudioService);
  private readonly zimService = inject(ZimService);
  private readonly broadcastService = inject(BroadcastService);
  private readonly localMediaPermissionService = inject(LocalMediaPermissionService);

  private modal = inject(NzModalService);
  private modalRef: NzModalRef | null = null;

  readonly callState = this.callStore.callState;
  readonly participants = this.callStore.participants;
  readonly currentUserId = this.zimService.userId;
  readonly micState = this.callStore.localMicState;
  readonly callStartTime = this.callStore.callStartTime;

  readonly isMicPermissionGranted = this.localMediaPermissionService.isMicPermissionGranted;

  readonly visible = this.callStore.showCallStatusBar;

  readonly statusColor = computed<'blue' | 'green' | null>(() => {
    const state = this.callStore.callState();

    if (state === 'connected') return 'green';
    if (state === 'outgoing' || state === 'ringing') return 'blue';

    return null;
  });

  get userName(): string {
    const call = this.callStore.currentCall();
    if (call) {
      return call.participants[0].userName
    }
    return ''
  }

  readonly label = computed(() => {
    const state = this.callStore.callState();

    // if (!this.isCaller() && state !== 'connected') return '';

    switch (state) {
      case 'outgoing':
        return 'Đang gọi ...';
      case 'ringing':
        return 'Đang đổ chuông ...';
      case 'connected':
        return 'Cuộc gọi thoại';
      default:
        return '';
    }
  });

  incomingState = signal<IncomingCallState>({ status: 'idle' });
  callDuration = signal('00:00:00');
  intervalId: any = null;

  constructor() {
    effect(() => {
      const call = this.callStore.pendingInvitation();
      const hasPendingCall = this.callStore.hasPendingCall();

      if (!call) {
        this.incomingState.set({ status: 'idle' });
        return;
      }

      if (this.callStore.isInCallRoute() && call.status !== 'waiting') {
        return;
      }

      // Bắt đầu load
      this.incomingState.set({ status: 'loading', call });

      this.zimService.getUserInfo([call.inviter]).then(res => {
        const inviter = res?.userList?.[0];
        if (!inviter) return;

        this.incomingState.set({
          status: 'ready',
          call,
          inviter
        });
      });

      // Hết incoming call → đóng modal
      if (!hasPendingCall && this.modalRef) {
        this.modalRef.close();
        this.modalRef = null;
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const state = this.incomingState();

      if (state.status !== 'ready') {
        this.modalRef?.close();
        this.modalRef = null;
        return;
      }

      if (!this.modalRef) {
        this.modalRef = this.modal.create({
          nzContent: IncomingCallModalComponent,
          nzFooter: null,
          nzClosable: false,
          nzMaskClosable: false,
          nzCentered: true,
          nzKeyboard: false,
          nzWidth: 480,
          nzData: {
            call: state.call,
            inviter: state.inviter
          }
        });
      }
    });

    effect(() => {
      if(this.callState() === 'connected' && this.callStartTime()) {
        if (this.intervalId) {
          clearInterval(this.intervalId);
        }
        const startMs = Date.now();
        this.intervalId = setInterval(() => {
          const diff = Date.now() - startMs;

          const h = Math.floor(diff / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          const s = Math.floor((diff % 60000) / 1000);

          this.callDuration.set(
            `${h.toString().padStart(2, '0')}:` +
            `${m.toString().padStart(2, '0')}:` +
            `${s.toString().padStart(2, '0')}`
          );
        }, 1000);
      }
    });

    this.broadcastService.call$.subscribe(({ action, payload }: CallActionData) => {
      if (this.callStore.isInCallRoute()) return;
      switch (action) {
        case 'sync-call-state':
          this.callStore.setState(payload);
          break;
        case 'sync-call-status':
          this.callStore.setCallState(payload.callState);
          break;
        case 'sync-mic-state':
          this.callStore.updateLocalMediaState('audio', payload.micState);
          break;
        case 'sync-call-start-time':
          this.callStore.setCallStartTime(payload.callStartTime);
          break;
        case 'clear-invitation':
          this.callStore.setPendingInvitation(null);
          break;
        case 'clear-call-status':
          this.callStore.setCallState('idle');
          break;
        case 'clear-ringtone':
          this.callAudio.stop();
          break;
        case 'reset-call-state':
          this.callStore.reset();
          break;
        case 'force-end-call':
          console.log("force-end-call", payload);
          this.callStore.forceEndCall(payload);
          break;
      }
    })

    //Yêu cầu tab call trả về state nếu là tab mới
    this.broadcastService.call({ action: 'request-call-state', payload: {} });
  }

  toggleMic() {
    this.broadcastService.call({ action: 'toggle-mic', payload: {} });
  }

  async endCall(): Promise<void> {
    const call = this.callStore.currentCall();
    if (!call) return;
    this.broadcastService.call({ action: 'end-call-in-call-startus-bar', payload: {} })
    if (this.callState() === 'outgoing' || this.callState() === 'ringing') {
      try {
        // Get invitees from participants
        const invitees = call.participants
        .filter(p => p.userId !== this.zimService.userId)
        .map(p => p.userId);

        if (invitees.length === 0) {
          console.warn('No invitees to cancel');
          return;
        }
        await this.callStore.cancelCall(invitees);
        return;
      } catch (error) {
        console.error('Failed to cancel call:', error);
        // TODO: Show error message
      }
    }

    if (this.callState() === 'connected') {
      if (call.inviter === this.zimService.userId) {
        try {
          await this.endCall();
          return;
        } catch (error) {
          console.log("endCall", error);
        }
      } else {
        try {
          await this.callStore.callQuit(call.callId);
          return;
        } catch (error) {
          console.log("callQuit", error);
        }
      }
    }
  }

  createModalPermission(type: "mic" | "cam") {
    const modalRef = this.modal.create<ModalPermissionComponent>({
      nzContent: ModalPermissionComponent,
      nzClosable: true,
      nzMaskClosable: false,
      nzCentered: true,
      nzWidth: '480px',
      nzFooter: null,
      nzData: {
        type,
        into: "meeting"
      },
    });

    const modalInstance = modalRef.getContentComponent();
    modalInstance?.onSubmitOutput.subscribe(() => { });
  }
}
